
// Professional analytics with Google Analytics
class Analytics {
  private isInitialized: boolean = false;
  private localEvents: any[] = [];
  private userId: string | null = null;
  private userNickname: string | null = null;
  private readonly GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || "";
  
  // Initialize analytics
  init() {
    if (this.isInitialized) return;
    
    this.isInitialized = true;
    
    // Load user info if available
    this.loadUserInfo();
    
    if (import.meta.env.PROD) {
      // Add Google Analytics script
      this.loadGoogleAnalytics();
      console.log('Google Analytics initialized');
    } else {
      // For development, keep using local storage
      this.loadLocalEvents();
      console.log('Local analytics initialized (development mode)');
    }
    
    // Track app initialization
    this.trackEvent('app_initialized');
  }
  
  // Load Google Analytics script
  private loadGoogleAnalytics() {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
    
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    gtag('js', new Date());
    gtag('config', this.GA_MEASUREMENT_ID);
    
    // Make gtag available globally
    window.gtag = gtag;
    
    // If we have user ID, set it for analytics
    if (this.userId) {
      gtag('set', 'user_id', this.userId);
      gtag('set', 'user_properties', {
        nickname: this.userNickname || 'Anonymous'
      });
    }
  }
  
  // Set user information for analytics
  setUserInfo(userId: string, nickname?: string) {
    this.userId = userId;
    this.userNickname = nickname || null;
    
    // Save to localStorage for persistence
    localStorage.setItem('analytics_user_id', userId);
    if (nickname) {
      localStorage.setItem('analytics_user_nickname', nickname);
    }
    
    // Update GA if already initialized
    if (this.isInitialized && import.meta.env.PROD && window.gtag) {
      window.gtag('set', 'user_id', userId);
      
      if (nickname) {
        window.gtag('set', 'user_properties', {
          nickname: nickname
        });
      }
    }
    
    this.trackEvent('user_identified', { userId, hasNickname: !!nickname });
  }
  
  // Load user info from localStorage
  private loadUserInfo() {
    const userId = localStorage.getItem('analytics_user_id');
    const nickname = localStorage.getItem('analytics_user_nickname');
    
    if (userId) {
      this.userId = userId;
      this.userNickname = nickname;
    }
  }
  
  // Load local events from localStorage
  private loadLocalEvents() {
    const savedEvents = localStorage.getItem('user_analytics');
    if (savedEvents) {
      try {
        this.localEvents = JSON.parse(savedEvents);
      } catch (error) {
        console.error('Failed to parse analytics data:', error);
        this.localEvents = [];
      }
    }
  }
  
  // Track a specific event
  trackEvent(eventName: string, eventData?: Record<string, any>) {
    if (!this.isInitialized) this.init();
    
    const event = {
      eventName,
      eventData: { ...eventData, userId: this.userId, nickname: this.userNickname },
      timestamp: Date.now()
    };
    
    if (import.meta.env.PROD && window.gtag) {
      // In production, use Google Analytics
      window.gtag('event', eventName, eventData);
    } else {
      // In development, use local storage
      this.localEvents.push(event);
      this.saveLocalEvents();
    }
    
    console.log('Analytics event:', event);
  }
  
  // Track page view
  trackPageView(pageName: string) {
    if (import.meta.env.PROD && window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: pageName,
        page_location: window.location.href,
        page_path: window.location.pathname,
        userId: this.userId,
        nickname: this.userNickname
      });
    } else {
      this.trackEvent('page_view', { page: pageName });
    }
  }
  
  // Track user actions
  trackUserAction(action: string, details?: Record<string, any>) {
    this.trackEvent('user_action', { action, ...details });
  }
  
  // Track lesson engagement
  trackLessonEngagement(lessonDay: number, action: string, details?: Record<string, any>) {
    this.trackEvent('lesson_engagement', { 
      day: lessonDay, 
      action,
      ...details
    });
  }
  
  // Track symptom data
  trackSymptoms(lessonDay: number, symptoms: string[]) {
    this.trackEvent('symptom_tracking', {
      day: lessonDay,
      symptoms,
      count: symptoms.length
    });
  }
  
  // Track program check-in
  trackProgramCheckIn(day: number, rating: number, feedback: string) {
    this.trackEvent('program_checkin', {
      day,
      rating,
      feedbackLength: feedback.length
    });
  }
  
  // Track admin actions
  trackAdminAction(action: string, details?: Record<string, any>) {
    this.trackEvent('admin_action', {
      action,
      ...details
    });
  }
  
  // Get user nickname
  getUserNickname(): string {
    return this.userNickname || 'Anonymous';
  }
  
  // Save events to localStorage (development only)
  private saveLocalEvents() {
    try {
      // Only keep last 100 events to avoid overflowing localStorage
      if (this.localEvents.length > 100) {
        this.localEvents = this.localEvents.slice(-100);
      }
      
      localStorage.setItem('user_analytics', JSON.stringify(this.localEvents));
    } catch (error) {
      console.error('Failed to save analytics data:', error);
    }
  }
}

// Add type definition for window.gtag and window.dataLayer
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Create singleton instance
const analytics = new Analytics();

export default analytics;
